import styled from "styled-components";
import theme from "../theme/theme";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { getTemporaryCredentials } from "../redux/actions";
import { SET_LOADING } from "../redux/action-types";
import LoadingBar from "../components/LoadingBar/LoadingBar";
import ErrorPopUp from "../components/ErrorPopUp/ErrorPopUp";
import { useHistory } from "react-router-dom";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import HeaderWithoutLogin from "../components/HeaderWithoutLogin";

const BodyWrapper = styled.div`
  width: 100%;
  min-height: calc(100vh - 75px);
  box-sizing: border-box;
  padding: 30px 0;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;

  @media (max-width: 480px) {
    min-height: calc(100vh - 65px);
  }
`;

const CardContainer = styled.section`
  display: flex;
  box-sizing: border-box;
  align-items: center;
  border-radius: 10px;
  padding: 30px;
  width: 40%;
  background-color: #fff;
  display: flex;
  flex-direction: column;

  @media (max-width: 480px) {
    width: 90%;
  }

  & h2 {
    font-weight: 700;
    font-size: 28px;
    color: ${theme.colors.primaryStrong2X};
    margin: 0;

    @media (max-width: 480px) {
      font-size: 28px;
    }
  }

  .login-information {
    width: 100%;
    height: 100%;
    display: flex;
    margin: 20px;

    .login-information__border-left {
      width: 5px;
      background-color: ${theme.colors.primaryStrong2X};
      border-radius: 15px;
      margin-left: 10px;
    }

    .login-information__user-info {
      margin-left: 30px;
      & div {
        display: flex;
        align-items: flex-end;

        p:first-child {
          font-weight: 700;
          margin-right: 5px;
          white-space: nowrap;
        }

        & p {
          margin: 10px 0 0 0;
        }

        .show-hide-copy {
          margin-left: 20px;
          color: ${theme.colors.primaryStrong2X};
          cursor: pointer;

          & p {
            margin-left: 5px;
          }
        }
      }
    }
  }

  .login-information__password-container {
    display: flex;
    flex-wrap: wrap;

    .inputContainer {
      width: 60%;

      @media (max-width: 480px) {
        width: 100%;
      }
    }

    & input {
      width: 100%;

      @media (max-width: 480px) {
        flex-wrap: nowrap;
      }
    }
  }

  & p {
    font-size: 13px;
    text-align: justify;
    margin: 15px 0;
  }

  .information-container {
    display: flex;
    align-items: center;
    margin: 20px 0;
    & > p {
      font-style: italic;
      margin: 0;
      word-wrap: break-word;
    }
  }

  .info-logo {
    min-height: 26px;
    min-width: 26px;
    border: 2px dashed ${theme.colors.primaryStrong2X};
    border-radius: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 10px;

    & p {
      font-weight: 700;
      font-size: 20px;
      color: ${theme.colors.primaryStrong2X};
      margin: 0;
    }
  }
`;

const ButtonContainer = styled.div`
  width: 40%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin: 20px 0;

  & button {
    width: 40%;
    color: #fff;
    border: none;
    font-size: 12px;
    font-weight: 700;
    padding: 10px;
    background-color: ${theme.colors.primaryStrong2X};
    border-radius: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    @media (max-width: 480px) {
      width: 100%;
    }
  }
`;

const TemporaryPassword = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { goal, loading, userAccountDetails } = useSelector(
    (state) => state.user
  );
  const [temporaryCredentials, setTemporaryCredentials] = useState({});
  const [error, setError] = useState(false);
  const [passwordShown, setPasswordShown] = useState(false);
  const [isLoaded, setIsLoaded] = useState(true);
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if ((temporaryCredentials.password || error) && userAccountDetails) {
      dispatch({ type: SET_LOADING, payload: false });
    } else if (error) {
      dispatch({ type: SET_LOADING, payload: false });
    } else {
      dispatch({ type: SET_LOADING, payload: true });
    }
  }, [dispatch, temporaryCredentials, loading, error, userAccountDetails]);

  useEffect(() => {
    if (loading) {
      setIsLoaded(false);
    }
  }, [loading]);

  useEffect(() => {
    (async () => {
      if (goal.id) {
        const credentials = await dispatch(getTemporaryCredentials(goal.id));
        if (credentials?.username) {
          setTemporaryCredentials(credentials);
        } else {
          setError(true);
        }
      } else {
        setError(true);
      }
    })();
  }, [dispatch, goal.id, setError]);

  return (
    <>
      {(!isLoaded || loading) && (
        <LoadingBar isLoading={loading} setIsLoaded={setIsLoaded} />
      )}
      {error && <ErrorPopUp />}
      <HeaderWithoutLogin
        isHelpButtonVisible={true}
        isLogOutButtonVisible={true}
      />
      <BodyWrapper>
        <CardContainer>
          <h2>
            You can find your temporary investment account password below.
            Please copy it and use it to create your unique login.
          </h2>
          <div className="login-information">
            <div className="login-information__border-left"></div>
            <div className="login-information__user-info">
              <h2>Login Information</h2>
              <div>
                <p>User ID: </p> <p>{temporaryCredentials.username}</p>
              </div>
              <div className="login-information__password-container">
                <div className="inputContainer">
                  <p>Temporary Password: </p>
                  <input
                    type={passwordShown ? "text" : "password"}
                    value={temporaryCredentials.password}
                  />
                </div>
                <div
                  className="show-hide-copy"
                  onClick={() => setPasswordShown((prev) => !prev)}
                >
                  {passwordShown ? (
                    <VisibilityOffIcon
                      fontSize="12px"
                      sx={{
                        cursor: "pointer",
                        color: `${isCopied ? "red" : "blue"}`,
                      }}
                    />
                  ) : (
                    <RemoveRedEyeIcon
                      fontSize="12px"
                      sx={{ cursor: "pointer" }}
                    />
                  )}

                  <p>{passwordShown ? "Hide" : "Show"} </p>
                </div>
                <div
                  className="show-hide-copy"
                  style={{
                    color: isCopied ? "gray" : theme.colors.primaryStrong2X,
                  }}
                  onClick={() => {
                    navigator.clipboard.writeText(
                      temporaryCredentials.password
                    );
                    setIsCopied(true);
                    setTimeout(() => {
                      setIsCopied(false);
                    }, 1000);
                  }}
                >
                  <ContentCopyIcon fontSize="12px" sx={{ cursor: "pointer" }} />
                  <p>{isCopied ? "Copied" : "Copy"}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="information-container">
            <div className="info-logo">
              <p>i</p>
            </div>
            <p>
              Incomplete applications can be continued at any time from the
              Manage tab within your Tactive dashboard
            </p>
          </div>
        </CardContainer>
        <ButtonContainer>
          <button onClick={() => history.push("/strategy-screen")}>
            CONTINUE TO MY DASHBOARD <ArrowForwardIosIcon fontSize="14px" />
          </button>
        </ButtonContainer>
      </BodyWrapper>
    </>
  );
};

export default TemporaryPassword;
